import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { AssetListFilterSchema } from '@deepguide-ai/types'
import { Badge } from '@deepguide-ai/ui/badge'
import {
  ASSET_TYPE_GROUPS,
  ASSET_TYPE_LABELS,
  CLASSIFICATION_LABELS,
  CRITICALITY_LABELS,
  classificationColor,
  criticalityColor,
} from '@/pages/assets/type'
import { SearchableSelect } from '@deepguide-ai/ui/combobox'
import { Input } from '@deepguide-ai/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui/select'
import { TableEmptyState } from '@deepguide-ai/ui/table-empty-state'
import { TableSkeleton } from '@deepguide-ai/ui/table-skeleton'
import { formatDate } from '@/lib/format'
import { DEFAULT_PAGE_SIZE, paginationSearchSchema } from '@/lib/pagination'
import { trpc } from '@/lib/trpc'
import ScreenataPageHeader from '@/components/compliance/ScreenataPageHeader'
import { TablePaginationFooter } from '@/components/ui'

export const auditorAssetSearchSchema = AssetListFilterSchema.pick({
  search: true,
  assetType: true,
  category: true,
  criticality: true,
  dataClassification: true,
  scope: true,
}).extend(paginationSearchSchema.shape)

export function AuditorAssetsPage() {
  const { workspaceId, auditCycleId } = useParams({ strict: false })
  const search = auditorAssetSearchSchema.parse(useSearch({ strict: false }))
  const navigate = useNavigate()
  const page = search.page ?? 1
  const pageSize = search.pageSize ?? DEFAULT_PAGE_SIZE
  const assets = useQuery(
    trpc.auditor.getAssets.queryOptions({
      auditCycleId: auditCycleId!,
      page,
      pageSize,
      search: search.search,
      assetType: search.assetType,
      category: search.category,
      criticality: search.criticality,
      dataClassification: search.dataClassification,
      scope: search.scope,
    })
  )
  const update = (next: Partial<typeof search>) =>
    navigate({
      to: '/workspaces/$workspaceId/auditor-center/$auditCycleId/assets',
      params: { workspaceId: workspaceId!, auditCycleId: auditCycleId! },
      search: { ...search, page: 1, ...next },
      replace: true,
    })

  return (
    <div className='space-y-4 p-6'>
      <ScreenataPageHeader
        title='Assets'
        titleClassName='text-foreground'
        description='Current asset inventory. Read-only.'
      />
      <div className='flex flex-wrap items-center gap-3'>
        <SearchableSelect
          value={search.assetType ?? (search.category ? `cat:${search.category}` : 'all')}
          onValueChange={(value) => {
            if (value.startsWith('cat:')) {
              void update({
                assetType: undefined,
                category: value.slice(4) as NonNullable<typeof search.category>,
              })
            } else {
              void update({
                category: undefined,
                assetType: value === 'all' ? undefined : value as NonNullable<typeof search.assetType>,
              })
            }
          }}
          options={[
            { value: 'all', label: `All Types${assets.data ? ` (${assets.data.total})` : ''}` },
            ...ASSET_TYPE_GROUPS.flatMap((group) => [
              ...(group.category !== 'other'
                ? [{ value: `cat:${group.category}`, label: `All ${group.label}`, group: group.label }]
                : []),
              ...group.types.map((type) => ({
                value: type,
                label: ASSET_TYPE_LABELS[type],
                group: group.label,
              })),
            ]),
          ]}
          placeholder='Type'
          searchPlaceholder='Search asset types...'
          emptyMessage='No asset types found.'
          triggerProps={{ className: 'font-body h-8 w-[180px] rounded-sm border border-border-divider bg-card px-3 text-[12px] font-medium text-muted-foreground' }}
          contentProps={{ popoverOptions: { style: { width: '240px' } } }}
        />
        <RegisterFilterSelect
          value={search.criticality ?? 'all'}
          width='w-[140px]'
          placeholder='Criticality'
          options={[['all', 'All Criticality'], ...Object.entries(CRITICALITY_LABELS)]}
          onChange={(value) => void update({ criticality: value === 'all' ? undefined : value as NonNullable<typeof search.criticality> })}
        />
        <RegisterFilterSelect
          value={search.dataClassification ?? 'all'}
          width='w-[160px]'
          placeholder='Classification'
          options={[['all', 'All Classifications'], ...Object.entries(CLASSIFICATION_LABELS)]}
          onChange={(value) => void update({ dataClassification: value === 'all' ? undefined : value as NonNullable<typeof search.dataClassification> })}
        />
        <RegisterFilterSelect
          value={search.scope ?? 'all'}
          width='w-[140px]'
          placeholder='Scope'
          options={[
            ['all', 'All Scope'], ['in_scope', 'In scope'], ['out_of_scope', 'Out of scope'],
          ]}
          onChange={(value) => void update({ scope: value === 'all' ? undefined : value as NonNullable<typeof search.scope> })}
        />
        <Input
          aria-label='Search assets'
          placeholder='Search name or provider asset ID…'
          value={search.search ?? ''}
          onChange={(event) =>
            void update({ search: event.target.value || undefined })
          }
          className='w-full sm:ml-auto sm:max-w-xs'
        />
      </div>
      {assets.isPending && <TableSkeleton columns={8} rows={8} />}
      {assets.isError && <p role='alert'>{assets.error.message}</p>}
      {assets.data?.items.length === 0 ? (
        <TableEmptyState message='No assets recorded.' />
      ) : null}
      {assets.data && assets.data.items.length > 0 ? (
        <div className='border-border bg-card overflow-hidden rounded-sm border'>
          <div className='overflow-x-auto'>
            <table className='w-full min-w-[940px] text-left'>
              <thead className='bg-surface-subtle text-text-subtle text-[10px] font-semibold tracking-[1px] uppercase'>
                <tr>
                  {[
                    'ID',
                    'Asset',
                    'Type',
                    'Classification',
                    'Criticality',
                    'Environment',
                    'Owner',
                    'Last review',
                  ].map((label) => (
                    <th key={label} className='px-3 py-3'>
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {assets.data.items.map((item) => (
                  <tr
                    key={item.id}
                    className='border-border hover:bg-surface-subtle border-t'
                  >
                    <td className='text-cobalt-strong px-3 py-3 text-xs font-medium'>
                      <Link
                        to='/workspaces/$workspaceId/auditor-center/$auditCycleId/assets/$assetId'
                        params={{ workspaceId: workspaceId!, auditCycleId: auditCycleId!, assetId: item.id }}
                        search={search}
                        className='hover:underline'
                      >
                        {item.displayId}
                      </Link>
                    </td>
                    <td className='max-w-[240px] px-3 py-3'>
                      <Link
                        to='/workspaces/$workspaceId/auditor-center/$auditCycleId/assets/$assetId'
                        params={{ workspaceId: workspaceId!, auditCycleId: auditCycleId!, assetId: item.id }}
                        search={search}
                        className='block truncate text-xs font-medium hover:underline'
                      >
                        {item.name}
                      </Link>
                      <p className='text-text-subtle truncate text-[11px]'>
                        {item.provider}
                      </p>
                    </td>
                    <td className='px-3 py-3 text-xs'>
                      {ASSET_TYPE_LABELS[item.assetType] ?? item.assetType}
                    </td>
                    <td className='px-3 py-3'>
                      <Badge
                        className={`text-[10px] font-semibold uppercase ${classificationColor(item.dataClassification)}`}
                      >
                        {CLASSIFICATION_LABELS[item.dataClassification] ??
                          item.dataClassification}
                      </Badge>
                    </td>
                    <td className='px-3 py-3'>
                      <Badge className={`text-[10px] font-semibold uppercase ${criticalityColor(item.criticality)}`}>
                        {CRITICALITY_LABELS[item.criticality] ??
                          item.criticality}
                      </Badge>
                    </td>
                    <td className='px-3 py-3 text-xs capitalize'>
                      {item.environment ?? 'Not recorded'}
                    </td>
                    <td className='px-3 py-3 text-xs'>
                      {item.ownerRoleName ?? item.ownerLabel}
                    </td>
                    <td className='px-3 py-3 text-xs'>
                      {formatDate(item.lastReviewedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <TablePaginationFooter
            page={page}
            pageSize={pageSize}
            totalItems={assets.data.total}
            onPageChange={(nextPage) => void update({ page: nextPage })}
            onPageSizeChange={(nextPageSize) =>
              void update({ pageSize: nextPageSize })
            }
          />
        </div>
      ) : null}
      <p className='text-text-subtle text-[11px] leading-normal'>
        Current inventory, not an audit-period snapshot.
      </p>
    </div>
  )
}

function RegisterFilterSelect({
  value,
  placeholder,
  width,
  options,
  onChange,
}: {
  value: string
  placeholder: string
  width: string
  options: readonly (readonly [string, string])[]
  onChange: (value: string) => void
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={`font-body h-8 ${width} rounded-sm border border-border-divider bg-card px-3 text-[12px] font-medium text-muted-foreground`}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map(([optionValue, optionLabel]) => (
          <SelectItem key={optionValue} value={optionValue}>{optionLabel}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
